/**
 * React Query hooks for duty time data
 */

import { useQuery } from '@tanstack/react-query';
import {
  getPilotDutyStatus,
  getAllPilotsStatus,
  getDutyAlerts,
  getDutyHistory,
} from '@/services/dutyTimeService';
import type { PilotDutyStatus, DutyAlert, DutyPeriod, DutyAuditEntry } from '@/services/dutyTimeService';

/**
 * Hook to get a specific pilot's duty status.
 */
export function usePilotDutyStatus(pilotId: number | undefined) {
  return useQuery<PilotDutyStatus, Error>({
    queryKey: ['duty-time', 'status', pilotId],
    queryFn: () => getPilotDutyStatus(pilotId!),
    enabled: !!pilotId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchInterval: 1000 * 60 * 5, // Auto-refresh every 5 minutes
  });
}

/**
 * Hook to get all pilots' duty status (admin/scheduler).
 */
export function useAllPilotsStatus() {
  return useQuery<PilotDutyStatus[], Error>({
    queryKey: ['duty-time', 'all-status'],
    queryFn: () => getAllPilotsStatus(),
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 60 * 5,
  });
}

/**
 * Hook to get all active alerts across all pilots.
 */
export function useDutyAlerts() {
  return useQuery<Array<DutyAlert & { pilotId: number; pilotName: string }>, Error>({
    queryKey: ['duty-time', 'alerts'],
    queryFn: () => getDutyAlerts(),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to get duty history for a specific pilot.
 */
export function useDutyHistory(pilotId: number | undefined) {
  return useQuery<{ dutyPeriods: DutyPeriod[]; auditLog: DutyAuditEntry[] }, Error>({
    queryKey: ['duty-time', 'history', pilotId],
    queryFn: () => getDutyHistory(pilotId!),
    enabled: !!pilotId,
    staleTime: 1000 * 60 * 5,
  });
}
